import { NetworkStatus } from "@apollo/client";
import { tz } from "@date-fns/tz";
import {
  addDays,
  differenceInHours,
  endOfMonth,
  format,
  getHours,
  isBefore,
  isTomorrow,
  startOfDay,
} from "date-fns";
import { useCallback, useMemo, useReducer } from "react";
import type { CalendarDate, MarkedDates } from "rn-calendar-component";
import { formatDurationToMinutes } from "../helpers/formatters";
import { useGetCalendarAvailabilityQuery } from "../queries/Calendar.generated";

type ServiceCalendarState = {
  selectedHour: string;
  selectedDate: string;
  markedDates: MarkedDates;
};

type ServiceCalendarActions = {
  type: "selectHour" | "selectDate";
  payload: string;
};

type Params = {
  bundleSlug: string;
  calendarEndDate?: string;
  calendarStartDate?: string;
  date?: string;
  locationId: string;
  workerId?: string;
  timeConsumption: string;
};

const selectedDateTemplate = {
  backgroundColor: "#000054",
  color: "#ffffff",
  selected: true,
  inSeries: false,
  endingDay: false,
  startingDay: false,
};

const availableDateTemplate = {
  inSeries: false,
  color: "#000000",
  startingDay: true,
  endingDay: true,
  pastDay: false,
};

const getInitialState = (date?: string) => {
  if (!date) {
    return { markedDates: {}, selectedDate: "", selectedHour: "" };
  }
  const dateString = format(date, "yyyy-MM-dd");
  const selectedHour = format(date, "HH:mm");

  return {
    markedDates: { [dateString]: selectedDateTemplate },
    selectedDate: dateString,
    selectedHour,
  };
};

const reducer = (state: ServiceCalendarState, action: ServiceCalendarActions) => {
  switch (action.type) {
    case "selectDate": {
      const isSameDate = state.selectedDate === action.payload;
      return {
        selectedHour: "",
        selectedDate: isSameDate ? "" : action.payload,
        markedDates: isSameDate ? {} : { [action.payload]: selectedDateTemplate },
      };
    }

    case "selectHour": {
      return { ...state, selectedHour: action.payload };
    }

    default: {
      return state;
    }
  }
};

const generateHours = (bundleSlug: string) => (startingHour?: number, endingHour?: number) => {
  function generateNumbers(min: number, max: number): number[] {
    const result: number[] = [];
    for (let i = min; i <= max; i++) {
      result.push(i);
    }
    return result;
  }

  function addHalfHour(formattedHours: string[], hour: number) {
    const hourString = hour.toString();
    const hourExists = formattedHours?.some((time) => time?.startsWith(`${hourString}:`));

    if (hourExists) {
      const newTime = `${hourString}:30`;
      if (!formattedHours.includes(newTime)) {
        return [...formattedHours, newTime];
      }
    }

    return formattedHours;
  }

  const isReducedHours = bundleSlug === "generalne";

  const hours = isReducedHours ? [8, 9, 12] : generateNumbers(8, 18);

  const filteredHours = hours?.filter(
    (hour) => hour >= (startingHour || 0) && hour <= (endingHour || 24),
  );

  const formattedHours = filteredHours?.map((hour) => {
    const hourString = String(hour).padStart(2, "0");
    return `${hourString}:00`;
  });

  const hoursWithHalfHours = isReducedHours ? formattedHours : addHalfHour(formattedHours, 18);

  return hoursWithHalfHours;
};

const currentDate = new Date().toISOString();
export const useServiceCalendar = ({
  bundleSlug,
  calendarEndDate,
  calendarStartDate,
  locationId,
  timeConsumption,
  date,
  workerId,
}: Params) => {
  const [{ selectedHour, markedDates, selectedDate }, dispatch] = useReducer(
    reducer,
    getInitialState(date),
  );
  const markedDate = Object.keys(markedDates)[0] || "";

  const hoursGenerator = generateHours(bundleSlug);

  const cappedTimeConsumption = useMemo(() => {
    const duration = formatDurationToMinutes(timeConsumption) / 60;

    return duration >= 8 ? "PT8H" : timeConsumption;
  }, [timeConsumption]);

  const lastMonthDay = endOfMonth(currentDate).toString();

  const {
    data: calendarData,
    networkStatus,
    refetch,
  } = useGetCalendarAvailabilityQuery({
    variables: {
      data: {
        workerId,
        timeConsumption: cappedTimeConsumption,
        endDate: calendarEndDate || format(lastMonthDay, "yyyy-MM-dd"),
        startDate: calendarStartDate || format(currentDate, "yyyy-MM-dd"),
        locationId,
      },
    },
    skip: !cappedTimeConsumption,
    initialFetchPolicy: "network-only",
    nextFetchPolicy: "cache-and-network",
  });

  const calendarLoading = networkStatus === NetworkStatus.loading;
  const calendarFetch = networkStatus === NetworkStatus.setVariables;

  const { availableDates, markedAvailableDates } = useMemo(
    () =>
      calendarData?.generateAvailabilityCalendarForReservation?.reduce(
        (acc, item) => {
          const key = format(item.slotDate || "", "yyyy-MM-dd", { in: tz("Europe/Warsaw") });
          const isNextDay =
            item?.slotDate && isTomorrow(new Date(item.slotDate), { in: tz("Europe/Warsaw") });

          const availableHours = hoursGenerator();

          const generateCalendarSlots =
            item.slots
              ?.concat()
              ?.sort?.((a, b) => {
                const dateA = a.startTime ? new Date(a.startTime) : "";
                const dateB = b.startTime ? new Date(b.startTime) : "";
                return isBefore(dateA, dateB) ? -1 : 1;
              })
              .map((slot) => format(slot.startTime || "", "HH:mm", { in: tz("Europe/Warsaw") })) ||
            [];

          const slots = generateCalendarSlots.filter((slot) => availableHours.includes(slot));

          acc.availableDates[key] = isNextDay ? [] : slots;

          if (acc.availableDates[key]?.length > 0) {
            acc.markedAvailableDates[key] = availableDateTemplate;
          }

          return acc;
        },
        { availableDates: {}, markedAvailableDates: {} } as {
          availableDates: Record<string, string[]>;
          markedAvailableDates: MarkedDates;
        },
      ) || { availableDates: {}, markedAvailableDates: {} },
    [calendarData?.generateAvailabilityCalendarForReservation, hoursGenerator],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  const findHours = useCallback(
    (dateString: string) => {
      const hours = hoursGenerator();

      return hours?.map((hour) => {
        const isAvailable = availableDates?.[dateString]?.includes(hour);
        const diffInHours = differenceInHours(`${dateString} ${hour}` || "", currentDate, {
          in: tz("Europe/Warsaw"),
        });

        const isWithin24h = diffInHours < 24;

        return { available: Boolean(isAvailable && !isWithin24h), hour };
      });
    },
    [availableDates, hoursGenerator, currentDate],
  );

  const getAvailableHours = useCallback(
    (date: string) => {
      const diffInHours = differenceInHours(date || "", currentDate, {
        in: tz("Europe/Warsaw"),
      });
      const startingHour = diffInHours > 0 ? 0 : 23;
      const hours = hoursGenerator(startingHour);
      const availableHours = availableDates?.[date] || [];

      return hours?.filter((hour) => availableHours?.includes(hour));
    },
    [availableDates, hoursGenerator],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  const handleCalendarDayPress = useCallback(
    (date: Omit<CalendarDate, "dayOfWeek">) => {
      const startOfSelectedDay = startOfDay(date.dayString);

      if (getAvailableHours(date.dayString).length > 0) {
        const hoursCap = 24;

        const diffDate = startOfDay(currentDate);
        const diff = differenceInHours(startOfSelectedDay, diffDate, {
          in: tz("Europe/Warsaw"),
        });

        if (diff === 24 && getHours(currentDate, { in: tz("Europe/Warsaw") }) >= 18) return;

        if (diff >= hoursCap) {
          dispatch({ type: "selectDate", payload: date.dayString });
        }
      }
    },
    [getAvailableHours, currentDate],
  );

  const handleCalendarHourPress = useCallback(
    (hour: string) => dispatch({ type: "selectHour", payload: hour }),
    [],
  );

  const resetDateAndHour = useCallback(() => {
    dispatch({ type: "selectHour", payload: "" });
    dispatch({ type: "selectDate", payload: "" });
  }, []);

  return {
    availableHours: getAvailableHours(markedDate),
    possibleHours: hoursGenerator(),
    hours: findHours(markedDate),
    handleDayPress: handleCalendarDayPress,
    handleHourPress: handleCalendarHourPress,
    loading:
      calendarLoading ||
      (calendarFetch && !calendarData?.generateAvailabilityCalendarForReservation),
    markedDates: { ...markedAvailableDates, ...markedDates },
    selectedDate,
    selectedHour,
    startDate: addDays(currentDate, 1),
    resetDateAndHour,
    refetchCalendar: refetch,
  };
};
